import { useState } from 'react';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6i5n4cbcbcsu13rgdug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      id="contact"
      aria-label="Contact Charlie Peters – Meta Ads Specialist London"
      itemScope
      itemType="https://schema.org/ContactPage"
      className="py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left — info */}
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-pink-600 mb-3">
              Let's Work Together
            </p>
            <h2 className="text-4xl font-bold text-gray-900 leading-tight mb-6">
              Ready to scale your<br />
              <span className="text-pink-600">Meta performance?</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10">
              Tell me about your brand, your goals, and where you're at right now. I'll come back to you within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 flex-shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Fast response</p>
                  <p className="text-sm text-gray-500">I reply to all enquiries within 24 hours.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 flex-shrink-0">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">No obligation</p>
                  <p className="text-sm text-gray-500">A discovery call is always free — no pressure, no hard sell.</p>
                </div>
              </div>
            </div>

            {/* Contact details */}
            <div className="mt-10 pt-8 border-t border-rose-100 space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="w-7 h-7 flex items-center justify-center text-pink-500">
                  <i className="ri-map-pin-line text-base"></i>
                </div>
                <span>London, UK</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="w-7 h-7 flex items-center justify-center text-pink-500">
                  <i className="ri-mail-line text-base"></i>
                </div>
                <a href="mailto:charliegeorgep97@gmail.com" className="hover:text-pink-600 transition-colors">
                  charliegeorgep97@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="w-7 h-7 flex items-center justify-center text-pink-500">
                  <i className="ri-phone-line text-base"></i>
                </div>
                <a href="tel:+447946707154" className="hover:text-pink-600 transition-colors">
                  +44 7946 707154
                </a>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-rose-50/40 rounded-2xl p-8 shadow-sm border border-rose-100">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-pink-100 text-pink-600 mb-5">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Message sent!</h3>
                <p className="text-gray-500 text-sm">Thanks for reaching out. I'll be in touch within 24 hours.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-sm text-pink-600 hover:underline cursor-pointer"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                aria-label="Meta Ads enquiry form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Name <span className="text-pink-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Charlie Peters"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Email <span className="text-pink-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@brand.com"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Brand / Business Name
                  </label>
                  <input
                    type="text"
                    name="brand"
                    placeholder="Your brand name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Monthly Ad Spend
                  </label>
                  <select
                    name="monthly_ad_spend"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition cursor-pointer"
                  >
                    <option value="">Select a range...</option>
                    <option value="Under £1k">Under £1k / month</option>
                    <option value="£1k–£5k">£1k – £5k / month</option>
                    <option value="£5k–£15k">£5k – £15k / month</option>
                    <option value="£15k–£50k">£15k – £50k / month</option>
                    <option value="£50k+">£50k+ / month</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Tell me about your goals <span className="text-pink-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="What are you trying to achieve? Where are you struggling? What does success look like?"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition resize-none"
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{charCount}/500</p>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500 flex items-center gap-1.5">
                    <i className="ri-error-warning-line"></i>
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-gradient-to-r from-pink-600 to-pink-700 text-white py-3.5 rounded-lg text-sm font-semibold hover:from-pink-700 hover:to-pink-800 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === 'sending' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Sending...
                    </span>
                  ) : (
                    "Send Message"
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  No spam, ever. Your details are kept private.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
